import React from 'react';
import { Link } from 'react-router-dom';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  Home,
  FileText,
  PhoneCall,
  Scale,
  Gavel,
  Briefcase,
  Building,
  Users,
  ChevronRight
} from 'lucide-react';
import { navigationData } from '../../data/navigation';
import logo from '../../assets/AKY_Legal_Consultants_Logo.png';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  // Social media icons mapping
  const socialIcons = {
    Facebook: Facebook,
    Twitter: Twitter,
    LinkedIn: Linkedin,
    Instagram: Instagram
  };

  // Icon mapping for Quick Links
  const getQuickLinkIcon = (name) => {
    switch(name) {
      case 'Home': return <Home className="w-4 h-4 mr-2 text-gold" />;
      case 'Documentations': return <FileText className="w-4 h-4 mr-2 text-gold" />;
      case 'Contact Us': return <PhoneCall className="w-4 h-4 mr-2 text-gold" />;
      default: return <ChevronRight className="w-4 h-4 mr-2 text-gold" />;
    }
  };

  // Icon mapping for Practice Areas
  const getPracticeIcon = (area) => {
    switch(area) {
      case 'Family Matters': return <Scale className="w-4 h-4 mr-2 text-gold" />;
      case 'Civil Matters': return <Gavel className="w-4 h-4 mr-2 text-gold" />;
      case 'Criminal Matters': return <Briefcase className="w-4 h-4 mr-2 text-gold" />;
      default: return <Scale className="w-4 h-4 mr-2 text-gold" />;
    }
  };

  // Icon mapping for About Us
  const getAboutIcon = (item) => {
    switch(item) {
      case 'The Firm': return <Building className="w-4 h-4 mr-2 text-gold" />;
      case 'Our Leadership': return <Users className="w-4 h-4 mr-2 text-gold" />;
      default: return <ChevronRight className="w-4 h-4 mr-2 text-gold" />;
    }
  };

  return (
    <footer className="bg-white text-black border-t border-gray-200">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          
          {/* Company Info */}
          <div className="space-y-4">
            <img 
              src={logo} 
              alt="AKY Legal Consultants" 
              className="h-20 w-auto object-contain"
            />
            <p className="text-dark-gray text-sm leading-relaxed">
              Since 2016, AKY Legal Consultants has operated under the guiding principle: 
              "Where Justice Meets Compassion." Based in Mumbai, our firm provides sophisticated 
              legal counsel across the Bombay High Court and various judicial forums.
            </p>
            
            {/* Social Links */}
            <div className="flex space-x-4 pt-4">
              {navigationData.socialLinks?.map((social) => {
                const Icon = socialIcons[social.name];
                return Icon ? (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gold transition-colors duration-200"
                  >
                    <Icon className="w-5 h-5 text-dark-gray hover:text-white" />
                  </a>
                ) : null;
              })}
            </div>
          </div>

          {/* Quick Links with Icons */}
          <div className="ml-0 lg:ml-12">
            <h3 className="text-lg font-bold text-primary-blue mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {navigationData.navLinks
                .filter(link => link.name !== 'Practices' && link.name !== 'About Us')
                .map((link) => (
                  <li key={link.name}>
                    <Link 
                      to={link.path} 
                      className="text-dark-gray hover:text-gold transition-colors duration-200 font-medium flex items-center"
                    >
                      {getQuickLinkIcon(link.name)}
                      {link.name}
                    </Link>
                  </li>
                ))}
            </ul>
          </div>

          {/* Practice Areas with Icons */}
          <div>
            <h3 className="text-lg font-bold text-primary-blue mb-6">Practice Areas</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/practices/family-matters" className="text-dark-gray hover:text-gold transition-colors duration-200 font-medium flex items-center">
                  {getPracticeIcon('Family Matters')}
                  Family Matters
                </Link>
              </li>
              <li>
                <Link to="/practices/civil-matters" className="text-dark-gray hover:text-gold transition-colors duration-200 font-medium flex items-center">
                  {getPracticeIcon('Civil Matters')}
                  Civil Matters
                </Link>
              </li>
              <li>
                <Link to="/practices/criminal-matters" className="text-dark-gray hover:text-gold transition-colors duration-200 font-medium flex items-center">
                  {getPracticeIcon('Criminal Matters')}
                  Criminal Matters
                </Link>
              </li>
            </ul>
          </div>

          {/* About Us with Icons */}
          <div>
            <h3 className="text-lg font-bold text-primary-blue mb-6">About Us</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/the-firm" className="text-dark-gray hover:text-gold transition-colors duration-200 font-medium flex items-center">
                  {getAboutIcon('The Firm')}
                  The Firm
                </Link>
              </li>
              <li>
                <Link to="/the-leadership" className="text-dark-gray hover:text-gold transition-colors duration-200 font-medium flex items-center">
                  {getAboutIcon('Our Leadership')}
                  Our Leadership
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold text-primary-blue mb-6">Contact Us</h3>
            <ul className="space-y-4">
              {/* Address */}
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-gold mt-1 flex-shrink-0" />
                <span className="text-dark-gray text-sm">
                  {navigationData.address.line1}<br />
                  {navigationData.address.line2}<br />
                  {navigationData.address.line3}
                </span>
              </li>

              {/* Phone */}
              <li className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-gold mt-1 flex-shrink-0" />
                <div>
                  <a href={`tel:${navigationData.phoneLink}`} className="text-dark-gray hover:text-gold transition-colors duration-200 font-medium block">
                    {navigationData.phone}
                  </a>
                  <a href={`tel:919769415432`} className="text-dark-gray hover:text-gold transition-colors duration-200 font-medium block">
                    {navigationData.telephone}
                  </a>
                </div>
              </li>

              {/* Email */}
              <li className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-gold flex-shrink-0" />
                <a href={`mailto:${navigationData.email}`} className="text-dark-gray hover:text-gold transition-colors duration-200 font-medium">
                  {navigationData.email}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-200">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
    <div className="flex flex-col md:flex-row justify-between items-center text-sm text-dark-gray">
      <p>© 2026 Copy-rights reserved | Designed By: <a 
          href="https://100billiontech.com/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-gold hover:underline font-medium"
        >
          100billion Tech
        </a>
      </p>
    </div>
  </div>
</div>
    </footer>
  );
};

export default Footer;